import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { BarChart3, Circle, CircleDot, Info } from "lucide-react";

interface PIConfigurationTabProps {
  piWeight: number;
  useModeledPi: boolean;
  modeledPiPercent: number | undefined;
  onUseModeledChange: (useModeled: boolean) => void;
  onModeledPercentChange: (percent: number | undefined) => void;
}

export function PIConfigurationTab({
  piWeight,
  useModeledPi,
  modeledPiPercent,
  onUseModeledChange,
  onModeledPercentChange
}: PIConfigurationTabProps) {
  // When PI is reweighted to 0, show percentage out of 100 instead of weighted score
  const isReweighted = piWeight === 0;
  const effectiveMax = isReweighted ? 100 : piWeight;
  
  const currentScore = useModeledPi 
    ? (modeledPiPercent !== undefined 
        ? (isReweighted ? modeledPiPercent : (modeledPiPercent * piWeight / 100)) 
        : 0)
    : 0; // Calculated score not yet implemented
  const progressPercent = useModeledPi
    ? (modeledPiPercent || 0)
    : 0;

  // Circle dimensions for SVG (matching IA)
  const strokeWidth = 5;

  // Circular progress component (matching IA design exactly)
  const CircularProgress = ({ sz = 100 }: { sz?: number }) => {
    const r = (sz - strokeWidth) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (progressPercent / 100) * c;
    return (
      <div className="relative" style={{ width: sz, height: sz }}>
        <svg className="transform -rotate-90" width={sz} height={sz}>
          <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
          <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke="hsl(var(--primary))" strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} className="transition-all duration-500" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold text-gray-900">{Math.round(currentScore)}</span>
          <span className="text-xs text-gray-500">of {effectiveMax}{isReweighted ? '%' : ''}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* PI Score Method Box */}
      <Card>
        <CardContent className="py-5 px-6">
          <div className="flex items-center">
            <div className="flex flex-col pr-6 border-r border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Score Method</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => onUseModeledChange(false)} 
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-all w-full ${!useModeledPi ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  {!useModeledPi ? <CircleDot className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  Calculate from PI data
                </button>
                <button 
                  onClick={() => onUseModeledChange(true)} 
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-all w-full ${useModeledPi ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  {useModeledPi ? <CircleDot className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  Enter estimate
                </button>
                {useModeledPi && (
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number" 
                      min="0" 
                      max="100" 
                      placeholder="0-100" 
                      value={modeledPiPercent || ''} 
                      onChange={(e) => onModeledPercentChange(e.target.value ? parseFloat(e.target.value) : undefined)} 
                      className="w-[92px] h-9" 
                    />
                    <span className="text-sm text-muted-foreground">% expected PI score</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 px-6">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                PI Score Preview
              </h3>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                Promoting Interoperability contributes <strong className="text-gray-900">{piWeight}%</strong> to your final MIPS score
                {isReweighted && <span className="text-gray-500"> (reweighted)</span>}
              </p>
              <p className="text-sm text-amber-800 mt-2.5 leading-relaxed">
                {useModeledPi 
                  ? <>Using estimated score. Enter PI measure data in <strong>Gap Management</strong> for calculated scoring.</>
                  : <>Score will be calculated from PI data entered in <strong>Gap Management → PI tab</strong>.</>
                }
              </p>
            </div>
            <div className="flex-shrink-0 pl-6 border-l border-gray-200">
              {/* Show dash when reweighted, otherwise show score display or circular progress */}
              {isReweighted ? (
                <div className="text-center min-w-[100px]">
                  <div className="text-4xl font-bold text-gray-400">—</div>
                  <div className="text-sm text-gray-500 mt-1">Reweighted</div>
                </div>
              ) : !useModeledPi ? (
                <div className="text-center min-w-[100px]">
                  <div className="text-4xl font-bold text-gray-900 tabular-nums">
                    {Math.round(currentScore)}<span className="text-2xl text-gray-400 font-normal">/{piWeight}</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">Weighted Score</div>
                </div>
              ) : (
                <CircularProgress />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PI Objectives Info Card */}
      <Card className="bg-gray-50/50">
        <CardHeader className="pb-3">
          <CardTitle>PI Measures</CardTitle>
          <CardDescription>Configure PI measure data in Gap Management → PI tab</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              Promoting Interoperability (PI) measures your use of certified EHR technology. 
              PI scoring is based on performance rates for required measures.
            </p>
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <p>
                <strong className="text-gray-700">To calculate your PI score:</strong> Go to Gap Management → PI tab to enter your numerators, denominators, and attestations for each PI measure.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
